import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Check, Tag } from 'lucide-react';
import { getPublishedVendorDiscountCodes } from '../../services/discountCodeService';

const formatOffer = (row) => {
  if (row.discount_type === 'percentage') {
    return `${Number(row.discount_value)}% off`;
  }
  return `$${Number(row.discount_value).toFixed(2)} off`;
};

const formatDateRange = (from, until) => {
  const fmt = (v) => {
    if (!v) return null;
    return new Date(v).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  const a = fmt(from);
  const b = fmt(until);
  if (!a && !b) return null;
  if (a && b) return `${a} – ${b}`;
  if (b) return `Until ${b}`;
  if (a) return `From ${a}`;
  return null;
};

const VendorPublicDiscountCodes = ({ vendorId, toast }) => {
  const [copiedId, setCopiedId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['published-vendor-discount-codes', vendorId],
    queryFn: () => getPublishedVendorDiscountCodes(vendorId),
    enabled: !!vendorId,
    staleTime: 2 * 60 * 1000,
  });

  const codes = data?.data ?? [];

  const copy = async (row) => {
    try {
      await navigator.clipboard.writeText(row.code);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast?.success?.('Copied', 'Discount code copied to clipboard.');
    } catch {
      toast?.error?.('Copy failed', 'Select and copy the code manually.');
    }
  };

  if (!vendorId || isLoading || codes.length === 0) {
    return null;
  }

  return (
    <div className="border-b border-m-border-light pb-6">
      <div className="mb-4 flex items-center gap-2">
        <Tag className="h-5 w-5 text-m-ink" aria-hidden />
        <h2 className="marketing-display-md text-m-ink">Special offers</h2>
      </div>
      <p className="mb-4 text-sm text-m-ink-muted">
        Use these codes at checkout when you book this vendor.
      </p>
      <div className="space-y-3">
        {codes.map((row) => {
          const range = formatDateRange(row.valid_from, row.valid_until);
          return (
            <div
              key={row.id}
              className="flex flex-col gap-3 rounded-2xl border border-m-border-light bg-m-surface p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="font-mono text-lg font-medium tracking-wide text-m-ink">
                  {row.code}
                </div>
                <div className="mt-1 text-sm text-m-ink-muted">{formatOffer(row)}</div>
                {range ? (
                  <div className="mt-1 text-xs text-m-ink-faint">{range}</div>
                ) : null}
                {row.description ? (
                  <p className="mt-2 whitespace-pre-line text-sm text-m-ink-muted">
                    {row.description}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => copy(row)}
                className="marketing-btn marketing-btn-ink shrink-0 gap-2 text-sm"
              >
                {copiedId === row.id ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy code
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VendorPublicDiscountCodes;
